import AsyncData from '@sysarb/sysarb-api/models/common/AsyncData';
import { defineStore } from 'pinia';
import type MappingWorkEqualWorksTag from '@sysarb/sysarb-api/models/entities/MappingWorkEqualWorksTag';
import type MappingWorkConnectedWorksTag from '@sysarb/sysarb-api/models/entities/MappingWorkConnectedWorksTag';
import type MappingWorkConnectedWorksComment from '@sysarb/sysarb-api/models/entities/MappingWorkConnectedWorksComment';
import type MappingEmploymentTag from '@sysarb/sysarb-api/models/entities/MappingEmploymentTag';
import type { RemovableRef} from '@vueuse/core';
import { StorageSerializers, useLocalStorage, useSessionStorage } from '@vueuse/core';
import { Guid } from 'guid-typescript';
import type MappingFactorValue from '@sysarb/sysarb-api/models/entities/MappingFactorValue';
import type MappingEmploymentComment from '@sysarb/sysarb-api/models/entities/MappingEmploymentComment';
import type MappingEmploymentAction from '@sysarb/sysarb-api/models/entities/MappingEmploymentAction';
import type { IMappingWorkAvatar } from '@sysarb/sysarb-api/models/entities/Mapping';
import Mapping from '@sysarb/sysarb-api/models/entities/Mapping';
import { ConditionOperator, OrderByDirection } from '@sysarb/sysarb-api/models/common/selection/Selection';
import type { IMappingAvatar } from '@sysarb/sysarb-api/models/entities/MappingEmployment';
import { usePayAuditEntityStore } from '@/store/salarySurvey/payAuditEntityStore';
import type { IAddedEvent, IDeletedEvent, IPropertyUpdatedEvent } from '@sysarb/sysarb-api/models/common/event';
import { newApi } from '@/newApi';
import { RouteNames } from '@/models/enums/RouteNames';
import { Role } from '@sysarb/sysarb-types/service-base/enums';
import { useLifecycleHooks } from '@/composables/useLifecycleHooks';
import { useUser } from '@/login/composables/useUser';
import { useResgate } from '@/login/composables/useResgate';
import { useApi } from '@/common/ApiHelper';
import { EventService } from '@/services/EventService';

interface PayAuditState {
    _mappingId: RemovableRef<Guid | null>;
    _mappingEmploymentIndex: AsyncData<Map<string, number>>;
    _mappingsAnalyzeGroupByField: RemovableRef<string>;
    _mappingComparisons: RemovableRef<Map<Guid, Guid>>;
    _payAudits: Mapping[];
    _storeId: Guid;
    _resgate: ReturnType<typeof useResgate> | null
}

export interface IEntityDataStatePayload {
    id?: string,
    field?: string,
    value?: any,
    promise?: Promise<any>
}

async function fetchAdministratorPayAudit(): Promise<Mapping[]> {
    const api = useApi();
    const payAudits = await api.mapping.getBySelection({
        QueriedFields: ['Name', 'Date', 'Status', 'Country', 'LegalEntity'],
        OrderBy: [{
            FieldName: 'Name',
            SortOrder: OrderByDirection.Desc
        }]
    });

    return payAudits;
}

async function fetchOperationalAdministratorPayAudits(resgate: ReturnType<typeof useResgate> | null): Promise<Mapping[]> {
    if (resgate == null) throw new Error('Resgate not initialized');
    const payAudits: Mapping[] = [];
    const { currentOrganisationNodeId, name, instanceUserId } = useUser();
    if (currentOrganisationNodeId.value != null && instanceUserId.value != null) {
        const orgNode = currentOrganisationNodeId.value;

        const mappingsFromService = await newApi.payEquityAnalysis.getOperationalAdministratorMappings(resgate, orgNode.toLowerCase(), instanceUserId.value.toLowerCase());

        if (mappingsFromService != null) {
            mappingsFromService.forEach(mapping => {
                const payAudit = new Mapping({
                    id: Guid.parse(mapping.id),
                    status: { value: mapping.status },
                    date: new Date(mapping.date),
                    name: mapping.name,
                    operationalAdministrator: [{ id: Guid.parse(instanceUserId.value ?? ''), name: name.value ?? '' }],
                    country: mapping.country,
                    legalEntity: mapping.legalEntity != null ? { id: Guid.parse(mapping.legalEntity), name: '' } : undefined,
                });
                payAudits.push(payAudit);
            });
        }
    }
    return payAudits;
}

async function fetchReadonlyPayAudits(): Promise<Mapping[]> {
    const { currentOrganisationNodeId } = useUser();
    const api = useApi();

    if (currentOrganisationNodeId.value === null) {
        return [];
    }
    // the potential exists for the user to have more than the max allowed, if this is the case then we can batch fetch the mappings and remove any duplicates
    const childNodes = await api.organisationNode.getChildren(Guid.parse(currentOrganisationNodeId.value));
    const childNodeBatches = [];

    // batch the nodes
    while (childNodes.length > 0) {
        childNodeBatches.push(childNodes.splice(0, 2000));
    }

    // fetch the unique mappings using the batches (which now wont be unique)
    const mappingAvatarPromise: Promise<IMappingAvatar[]>[] = [];
    childNodeBatches.forEach(batch => {
        mappingAvatarPromise.push(api.mappingEmployment.getDistinctMappingIDValues({
            Condition: {
                PropertyName: 'organisationNode_ID',
                ConditionOperator: ConditionOperator.In,
                Value: batch.map(x => x.id)
            },
            QueriedFields: ['mappingID']
        }));
    });
    // await the result of the promises and filter any duplicates
    const mappingsFromPromises: IMappingAvatar[] = [];
    await Promise.all(mappingAvatarPromise).then(mappingResults => {
        mappingResults.forEach(result => mappingsFromPromises.push(...result.filter(x => !mappingsFromPromises.some(y => y.id.isEqualTo(x.id)))));
    });

    // fetch the mapping data
    return api.mapping.getBySelection({
        Condition: {
            PropertyName: 'ID',
            ConditionOperator: ConditionOperator.In,
            Value: mappingsFromPromises.map(x => x.id)
        },
        QueriedFields: ['Name', 'Date', 'Status', 'Country', 'LegalEntity'],
        OrderBy: [{
            FieldName: 'Name',
            SortOrder: OrderByDirection.Asc
        }]
    });
}

async function fetchAnalystPayAudits(organisationNodeId: string | null): Promise<Mapping[]> {
    if (organisationNodeId === null) return [];
    const api = useApi();
    // the potential exists for the user to have more than the max allowed, if this is the case then we can batch fetch the mappings and remove any duplicates
    const childNodes = await api.organisationNode.getChildren(Guid.parse(organisationNodeId));
    const childNodeBatches = [];

    // batch the nodes
    while (childNodes.length > 0) {
        childNodeBatches.push(childNodes.splice(0, 2000));
    }

    // fetch the unique mappings using the batches (which now wont be unique)
    const mappingAvatarPromise: Promise<IMappingWorkAvatar[]>[] = [];
    childNodeBatches.forEach(batch => {
        mappingAvatarPromise.push(api.mappingEmployment.getDistinctMappingIDValues({
            Condition: {
                PropertyName: 'organisationNode_ID',
                ConditionOperator: ConditionOperator.In,
                Value: batch.map(x => x.id)
            },
            QueriedFields: ['mappingID']
        }));
    });
    // await the result of the promises and filter any duplicates
    const mappingsFromPromises: IMappingAvatar[] = [];
    await Promise.all(mappingAvatarPromise).then(mappingResults => {
        mappingResults.forEach(result => mappingsFromPromises.push(...result.filter(x => !mappingsFromPromises.some(y => y.id.isEqualTo(x.id)))));
    });

    // fetch the mapping data
    return api.mapping.getBySelection({
        Condition: {
            PropertyName: 'ID',
            ConditionOperator: ConditionOperator.In,
            Value: mappingsFromPromises.map(x => x.id)
        },
        QueriedFields: ['Name', 'Date', 'Status', 'Country', 'LegalEntity'],
        OrderBy: [{
            FieldName: 'Name',
            SortOrder: OrderByDirection.Asc
        }]
    });
}

const storeDef = defineStore('payAudit', {
    state: (): PayAuditState => ({
        _mappingId: useSessionStorage<Guid | null>('mappingComparisons', null, { serializer: StorageSerializers.object }),
        _mappingEmploymentIndex: new AsyncData<Map<string, number>>(),
        _mappingsAnalyzeGroupByField: useLocalStorage<string>('groupingField', 'gender.value', { serializer: StorageSerializers.string }),
        _mappingComparisons: useLocalStorage<Map<Guid, Guid>>('mappingComparisons', new Map(), { serializer: StorageSerializers.map }),
        _payAudits: [],
        _storeId: Guid.create(),
        _resgate: useResgate(crypto.randomUUID())
    }),
    getters: {
        mappingEmploymentIndex: state => state._mappingEmploymentIndex,
        mappingEmployments: () => usePayAuditEntityStore().$state._mappingEmployment,
        mappingWorkEqualWorksTags: () => usePayAuditEntityStore().$state._mappingWorkEqualWorksTag,
        mappingWorkConnectedWorksTags: () => usePayAuditEntityStore().$state._mappingWorkConnectedWorksTag,
        mappingWorkConnectedWorksComments: () => usePayAuditEntityStore().$state._mappingWorkConnectedWorksComment,
        mappingsAnalyzeGroupByField: state => state._mappingsAnalyzeGroupByField,
        mappingWorkValuations: () => usePayAuditEntityStore().$state._mappingWorkValuation,
        mapping: () => usePayAuditEntityStore().$state._mapping,
        mappingEmploymentTags: () => usePayAuditEntityStore().$state._mappingEmploymentTag,
        mappingComparisons: state => state._mappingComparisons,
        mappingId: state => state._mappingId,
        mappingWorks: () => usePayAuditEntityStore().$state._mappingWork,
        mappingFactors: () => usePayAuditEntityStore().$state._mappingFactor,
        mappingFactorValues: () => usePayAuditEntityStore().$state._mappingFactorValue,
        mappingEmploymentComments: () => usePayAuditEntityStore().$state._mappingEmploymentComment,
        mappingEmploymentActions: () => usePayAuditEntityStore().$state._mappingEmploymentAction,
        mappingFactorsByID: () => usePayAuditEntityStore().mappingFactorsByID,
        mappingFactorValuesByMappingFactorID: () => usePayAuditEntityStore().mappingFactorValuesByMappingFactorID,
        payAudits: state => state._payAudits,
        operationalAdministratorAvatar: () => {
            const { isOperationalAdministrator, instanceUserId, name } = useUser();
            if (isOperationalAdministrator.value && instanceUserId.value != null) {
                return [{ id: Guid.parse(instanceUserId.value), name: name.value ?? '' }];
            }
            return [];
        }
    },
    actions: {
        setEntityStoreData(storePropertyToUpdate: string, payload: IEntityDataStatePayload) {
            usePayAuditEntityStore().setEntityStoreData(storePropertyToUpdate, payload);
        },
        addEntityStoreData(storePropertyToUpdate: string, payload: IEntityDataStatePayload) {
            usePayAuditEntityStore().addEntityStoreData(storePropertyToUpdate, payload);
        },
        deleteEntityStoreData(storePropertyToUpdate: string, payload: IEntityDataStatePayload) {
            usePayAuditEntityStore().deleteEntityStoreData(storePropertyToUpdate, payload);
        },
        setMappingEmploymentIndexStoreData(mappingEmploymentIndex: AsyncData<Map<string, number>>) {
            this._mappingEmploymentIndex = mappingEmploymentIndex;
        },
        deleteMappingEmploymentIndexStoreData(mappingEmploymentId: string) {
            this._mappingEmploymentIndex.data.delete(mappingEmploymentId);
        },
        updateEntityStoreData(storePropertyToUpdate: string, payload: IEntityDataStatePayload) {
            usePayAuditEntityStore().updateEntityStoreData(storePropertyToUpdate, payload);
        },
        addMappingWorkEqualWorksTag(mappingWorkEqualWorksTag: MappingWorkEqualWorksTag) {
            usePayAuditEntityStore().addMappingWorkEqualWorksTag(mappingWorkEqualWorksTag);
        },
        addMappingWorkConnectedWorksTag(mappingWorkConnectedWorksTag: MappingWorkConnectedWorksTag) {
            usePayAuditEntityStore().addMappingWorkConnectedWorksTag(mappingWorkConnectedWorksTag);
        },
        addMappingEmploymentTag(mappingEmploymentTag: MappingEmploymentTag) {
            usePayAuditEntityStore().addMappingEmploymentTag(mappingEmploymentTag);
        },
        addMappingWorkConnectedWorksComment(mappingWorkConnectedWorksComment: MappingWorkConnectedWorksComment) {
            usePayAuditEntityStore().addMappingWorkConnectedWorksComment(mappingWorkConnectedWorksComment);
        },
        addMappingEmploymentComment(mappingEmploymentComment: MappingEmploymentComment) {
            usePayAuditEntityStore().addMappingEmploymentComment(mappingEmploymentComment);
        },
        addMappingEmploymentAction(mappingEmploymentAction: MappingEmploymentAction) {
            usePayAuditEntityStore().addMappingEmploymentAction(mappingEmploymentAction);
        },
        addMappingFactorValue(mappingFactorValue: MappingFactorValue) {
            usePayAuditEntityStore().addMappingFactorValue(mappingFactorValue);
        },
        setMappingsAnalyzeGroupByField(field: string) {
            this._mappingsAnalyzeGroupByField = field;
        },
        addMappingComparison(mappingComparison: { originator: Guid; comparitor: Guid }) {
            this._mappingComparisons.set(mappingComparison.originator, mappingComparison.comparitor);
        },
        deleteMappingComparison(mappingComparison: Guid) {
            this._mappingComparisons.delete(mappingComparison);
        },
        async createNewMapping(newPayAudit: Mapping): Promise<void> {
            const { role } = useUser();
            const api = useApi();
            const result = await api.mapping.create(newPayAudit);

            if (result.id == null) throw Error('New Salary survey is missing id');

            let newRoute;
            // Route to the created mapping
            switch (role.value?.role) {
                case Role.OperationalAdministrator:
                    newRoute = RouteNames.OperationalAdministratorMappings;
                    break;
                case Role.SystemAdministrator:
                case Role.Administrator:
                    newRoute = RouteNames.AdministratorPayEquityAuditOverview;
                    break;
                default:
                    throw new Error(`${role.value?.role} role not supported`);
            }

            // Route to the created mapping
            void this.router.push({
                name: newRoute,
                params: {
                    id: result.id.toString()
                }
            });
        },
        async loadPayAudits() {
            const { role, currentOrganisationNodeId } = useUser();
            this._payAudits = [];

            switch (role.value?.role) {
                case Role.OperationalAdministrator:
                    this._payAudits = await fetchOperationalAdministratorPayAudits(this._resgate);
                    break;
                case Role.Administrator:
                case Role.Root:
                case Role.SystemAdministrator:
                case Role.Consultant:
                    this._payAudits = await fetchAdministratorPayAudit();
                    break;
                case Role.ReadOnly:
                    this._payAudits = await fetchReadonlyPayAudits();
                    break;
                case Role.SalarySurveyAnalyst:
                    this._payAudits = await fetchAnalystPayAudits(currentOrganisationNodeId.value);
                    break;
                default:
                    break;
            }
        },
        initPayAuditSubscription(): void {
            EventService.getInstance(this.router).subscribe({
                componentId: this._storeId as Guid,
                entity: 'Mapping',
                addedCallback: (event: IAddedEvent) => {
                    const payAudit = event.entity as Mapping;
                    // Add item if it matches the current criterias.
                    if (this._payAudits != null && !this._payAudits.some(x => x.id?.toString() === payAudit.id?.toString())) {
                        // Set mapping and filter
                        this._payAudits.push(payAudit);
                    }
                },
                propertyUpdatedCallback: (event: IPropertyUpdatedEvent) => {
                    const changedMappingIndex = this._payAudits.findIndex(x => x.id?.toString() === event.entityID.toString());
                    if (changedMappingIndex !== -1) {
                        if (event.propertyName === 'name') {
                            this._payAudits[changedMappingIndex].name = event.propertyValue;
                        }
                        if (event.propertyName === 'date') {
                            // Set mapping and filter
                            this._payAudits[changedMappingIndex].date = event.propertyValue;
                        }
                    }
                },
                deletedCallback: (event: IDeletedEvent) => {
                    const deletedMappingIndex = this._payAudits.findIndex(x => x.id?.toString() === event.entityID.toString());
                    if (deletedMappingIndex !== -1) {
                        this._payAudits.splice(deletedMappingIndex, 1);
                    }
                }
            });
        },
    },
    onDeactivated() {
        this._resgate?.unsubscribe();
        EventService.getInstance(this.router).unsubscribeComponent(this._storeId as Guid);
    }
});

export const usePayAuditStore = (): ReturnType<typeof storeDef> => useLifecycleHooks(storeDef());
